import { IStreamDeclaration } from '@infotech-soe/merps/types/streams';
import { TControlName, tg } from '@infotech-soe/metaschema';

import { AppDispatch } from '../..';
import IApiProvider, {
  TActionArgsValues,
} from '../../../infrastructure/providers/abstract/IApiProvider';
import MerpsApiProvider from '../../../infrastructure/providers/pb-adapter/MerpsApiProvider';
import { CategoryOpenMode } from '../../../models/category/category';
import { HookTypes } from '../../../models/category/HookTypes';
import CategoryConditionsService from '../../../services/category/CategoryConditionsService';
import ICategoryConditionsService from '../../../services/category/ICategoryConditionsService';
import ContextService from '../../../services/context/ContextService';
import IContextService, {
  ClientContext,
} from '../../../services/context/IContextService';
import FormService from '../../../services/form/FormService';
import trimControlValue from '../../../services/form/helpers/trimControlValue';
import IFormService, {
  FormUpdateStrategy,
} from '../../../services/form/IFormService';
import { TreeFormFieldNames } from '../../../services/form/schema-factory/TreeFormSchemaFactory';
import {
  isConditionForm,
  isPredefinedForm,
} from '../../../services/form/typeguards';
import {
  FileInputFormControlViewModel,
  FormControlViewModelType,
  ImageInputFormControlViewModel,
} from '../../../services/form/view-model/FormControlViewModel';
import {
  PredefinedForms,
  PredefinedReviewFormCreateViewModel,
} from '../../../services/form/view-model/FormCreateViewModel';
import {
  CategoryFormSource,
  FormSourceType,
} from '../../../services/form/view-model/FormSource';
import { hiddenFieldsInRows } from '../../../ui/components/dataset/utils/hiddenFieldsInRows';
import { AppState } from '../../reducers/root';
import { getCatalogsStateEntity } from '../../selectors/catalogs';
import {
  getCategoryOpenMode,
  getCategoryState,
} from '../../selectors/category';
import {
  filterDatasetsForCategory,
  getDatasetCategoryId,
  getDatasetName,
} from '../../selectors/dataset';
import {
  getControlValue,
  getFormCommandName,
  getFormContext,
  getFormParent,
  getFormState,
  getReviewForm,
} from '../../selectors/form';
import { getHierarchyStateEntity } from '../../selectors/hierarchy';
import { getReviewFormData } from '../../selectors/reviewFormData';
import { getSessionApplication } from '../../selectors/session';
import actions from '../../slices/actions';
import catalogs from '../../slices/catalogs';
import categoryActions from '../../slices/category';
import categoryHook from '../../slices/categoryHook';
import commands from '../../slices/commands';
import dictionary from '../../slices/dictionary';
import form, {
  CreateConditionComplexFormCreatePayload,
  CreateDefaultComplexFormPayload,
  CreatePredefinedComplexFormCreatePayload,
  FormCloseReasons,
  FormParentType,
} from '../../slices/form';
import hierarchy from '../../slices/hierarchy';
import { apiCall, createAppListener, postDispatch } from '../listenerHelpers';

const formListener = createAppListener();

const apiProvider: IApiProvider = new MerpsApiProvider();
const conditionsService: ICategoryConditionsService =
  new CategoryConditionsService();
const contextService: IContextService = new ContextService();
const formService: IFormService = new FormService();

const validateField = (
  { field, formId, value }: ReturnType<typeof form.update>['payload'],
  state: AppState,
  dispatch: AppDispatch
) => {
  const { source } = getFormState(state, formId);

  if (
    isPredefinedForm(source) &&
    source.action === PredefinedForms.TREE &&
    source.treeId
  ) {
    const catalogsStateEntity = getCatalogsStateEntity(state, source.treeId);
    const hierarchyStateEntity = getHierarchyStateEntity(state, source.treeId);

    const treeNode =
      catalogsStateEntity?.nodes.find(({ id }) => value === id) ||
      hierarchyStateEntity?.nodes.find(({ id }) => value === id);

    if (treeNode) {
      dispatch(
        form.validateField({ formId, field, invalid: !treeNode.hasAccess })
      );
    }
  }
};

const uploadFormFiles = async (
  files: Record<TControlName, File>
): Promise<Record<TControlName, IStreamDeclaration>> => {
  const ids: Record<TControlName, IStreamDeclaration> = {};

  for (const [control, file] of Object.entries(files)) {
    const buffer = await file.arrayBuffer();
    ids[control] = { id: await apiProvider.streams.write(buffer) };
  }

  return ids;
};

const onCreatePredefinedForm = async (
  payload: CreatePredefinedComplexFormCreatePayload
): Promise<ReturnType<typeof form.createSuccess>['payload']> => {
  let createdForm;
  const { parent, ...restOfPayload } = payload;
  if (parent && restOfPayload.subtype === PredefinedForms.REVIEW) {
    createdForm = await formService.create({
      ...restOfPayload,
      dataset: parent.id,
    });
  } else if (restOfPayload.subtype === PredefinedForms.TREE) {
    createdForm = await formService.create({
      ...restOfPayload,
      action: '',
    });
  } else {
    throw new Error(`Unsupported predefined form subtype`);
  }

  return {
    form: createdForm,
    parent,
    command: payload.command,
    context: new ClientContext(),
  };
};

const onCreateDefaultOrConditionComplexForm = async (
  payload:
    CreateDefaultComplexFormPayload | CreateConditionComplexFormCreatePayload,
  state: AppState
): Promise<ReturnType<typeof form.createSuccess>['payload']> => {
  const categoryId = getDatasetCategoryId(state, payload.dataset);
  const categoryState = getCategoryState(state, categoryId);
  const datasets = filterDatasetsForCategory(state, categoryId);
  const application = getSessionApplication(state);

  const createdForm = await formService.create({
    ...payload,
    datasetsState: datasets,
    categoryState,
    application: application || '',
  });

  return {
    form: createdForm,
    parent: {
      type: FormParentType.DATASET,
      id: payload.dataset,
    },
    command: payload.command,
    context:
      payload.type === FormSourceType.FORM
        ? payload.context
        : await contextService.getContext(
            categoryState,
            state.dataset,
            payload.dataset
          ),
  };
};

formListener.startListening(
  apiCall(
    {
      request: form.create,
      success: form.createSuccess,
      failure: form.createFailure,
    },
    async (payload, state) => {
      if (payload.type === FormSourceType.PREDEFINED) {
        return onCreatePredefinedForm(payload);
      }
      return onCreateDefaultOrConditionComplexForm(payload, state);
    }
  )
);

formListener.startListening(
  apiCall(
    {
      request: form.update,
      success: form.updateSuccess,
      failure: form.updateFailure,
    },
    async (payload, state, dispatch) => {
      validateField(payload, state, dispatch);

      const { value, formId, field } = payload;
      const formState = getFormState(state, formId);
      const patch = await formService.update(
        formState,
        { [field]: value },
        FormUpdateStrategy.UPDATE
      );

      return {
        formId,
        controls: patch.controls,
        args: patch.args,
        skipNextUpdater: false,
        apiVars: patch.apiVars,
      };
    }
  )
);

formListener.startListening(
  apiCall(
    {
      request: form.updateWithOutParams,
      success: form.updateWithOutParamsSuccess,
      failure: form.updateWithOutParamsFailure,
    },
    async ({ field, formId, value }, state) => {
      const formState = getFormState(state, formId);
      const category = getCategoryState(
        state,
        (formState.source as CategoryFormSource).categoryId
      );
      const patch = await formService.updateWithOutParams(
        formState,
        field,
        { ...value },
        category,
        state.dataset
      );

      return {
        formId,
        controls: patch.controls,
        args: patch.args,
        apiVars: patch.apiVars,
        skipNextUpdater: patch.skipNextUpdater,
      };
    }
  )
);

formListener.startListening(
  apiCall(
    {
      request: form.runSchemaUpdater,
      success: form.runSchemaUpdaterSuccess,
      failure: form.runSchemaUpdaterFailure,
    },
    async ({ formId, field, value }, state) => {
      const formState = getFormState(state, formId);
      const category = getCategoryState(
        state,
        (formState.source as CategoryFormSource).categoryId
      );
      const patch = await formService.runFormSchemaUpdater(
        formState,
        field,
        value,
        category,
        state.dataset
      );

      return {
        formId,
        controls: patch.controls,
        args: patch.args,
        skipNextUpdater: false,
        apiVars: patch.apiVars,
      };
    }
  )
);

formListener.startListening(
  apiCall(
    {
      request: form.updateReviewForm,
      success: form.updateReviewFormSuccess,
      failure: form.updateReviewFormFailure,
    },
    async ({ categoryId, datasetId }, state) => {
      const emptyResult = {
        formId: '',
        controls: {},
        args: {},
        skipNextUpdater: false,
        apiVars: new Map(),
      };

      const reviewForm = getReviewForm(state, datasetId);
      if (!reviewForm) return emptyResult;

      const category = getCategoryState(state, categoryId);
      const ctx = await contextService.getContext(
        category,
        state.dataset,
        datasetId
      );
      const reviewData = getReviewFormData(state, datasetId);

      const { source: reviewFormSource } = reviewForm.form;
      if (!isPredefinedForm(reviewFormSource)) {
        throw new Error('no way');
      }
      if (reviewFormSource.action !== PredefinedForms.REVIEW) {
        throw new Error('no way');
      }

      let values, variables;
      if (reviewData.isAuto) {
        values = reviewData.data;
        variables = {
          title: reviewData.title,
          titleColor: reviewData.titleColor,
          titleTextColor: reviewData.titleTextColor,
          backgroundColor: reviewData.backgroundColor,
          textColor: reviewData.textColor,
        };
      } else {
        const { data, opts } =
          await apiProvider.print.getReportDataFromLibraryRoutine(
            ctx.get('MasterCategory'),
            ctx.get('DisplayMode'),
            category.datasets[datasetId].name,
            reviewData.recordViewName,
            ctx.get('Id')
          );
        values = data;
        variables = opts?.variables;
      }

      const model: PredefinedReviewFormCreateViewModel = {
        dataset: datasetId,
        type: FormSourceType.PREDEFINED,
        subtype: PredefinedForms.REVIEW,
        values,
        category: ctx.get('Category'),
        template: reviewFormSource.template,
        variables,
        width: reviewFormSource.width,
      };

      const replaced = await formService.create(model);

      return {
        formId: reviewForm.form.id,
        controls: replaced.controls,
        args: replaced.args,
        skipNextUpdater: false,
        apiVars: replaced.apiVars,
      };
    }
  )
);

formListener.startListening(
  apiCall(
    {
      request: form.runSingleUpdater,
      success: form.runSingleUpdaterSuccess,
      failure: form.runSingleUpdaterFailure,
    },
    async ({ formId, field, name }, state) => {
      const formState = getFormState(state, formId);
      const category = getCategoryState(
        state,
        (formState.source as CategoryFormSource).categoryId
      );
      const datasets = state.dataset;

      const result = await formService.runSingleUpdater(
        formState,
        field,
        category,
        datasets,
        name
      );

      return {
        formId,
        controls: result.controls,
        args: result.args,
        skipNextUpdater: false,
        apiVars: result.apiVars,
      };
    }
  )
);

formListener.startListening(
  apiCall(
    {
      request: form.saveHistory,
      success: form.saveHistorySuccess,
      failure: form.saveHistoryFailure,
    },
    async ({ formState }) => {
      await formService.saveConfig(formState);
    }
  )
);

formListener.startListening(
  postDispatch(form.sign, ({ formId, names, signatures }, _state, dispatch) => {
    names.forEach((name, i) => {
      dispatch(
        form.updateControlProp({
          formId,
          field: name,
          propName: 'value',
          propValue: signatures[i],
        })
      );
      dispatch(
        form.runSchemaUpdater({ formId, field: name, value: signatures[i] })
      );
    });
  })
);

formListener.startListening(
  apiCall(
    {
      request: form.clear,
      success: form.updateSuccess,
      failure: form.updateFailure,
    },
    async ({ formId }, state) => {
      const formState = getFormState(state, formId);
      const category = getCategoryState(
        state,
        (formState.source as CategoryFormSource).categoryId
      );
      const patch = await formService.clearForm(
        formState,
        category,
        state.dataset
      );
      return { formId, controls: patch.controls, args: patch.args };
    }
  )
);

formListener.startListening(
  postDispatch(form.updateSuccess, ({ formId }, state, dispatch) => {
    const parent = getFormParent(state, formId);
    if (!parent) return;

    const { id: dictionaryId, type } = parent;

    if (type === FormParentType.DICTIONARY) {
      dispatch(dictionary.updateOutputs({ id: dictionaryId }));
    }
  })
);

formListener.startListening(
  apiCall(
    {
      request: form.close,
      success: form.closeSuccess,
      failure: form.closeFailure,
    },
    async ({ formId, reason = FormCloseReasons.COMMON }, state, dispatch) => {
      const formState = getFormState(state, formId);
      const parent = getFormParent(state, formId);

      dispatch(form.saveHistory({ formState }));

      return { formId, formSource: formState.source, reason, parent };
    }
  )
);

formListener.startListening(
  postDispatch(
    form.closeSuccess,
    ({ formSource, reason, parent }, state, dispatch) => {
      const treeId = isPredefinedForm(formSource) && formSource.treeId;

      if (
        (isConditionForm(formSource) ||
          formSource.type === FormSourceType.FORM) &&
        formSource.hook &&
        reason === FormCloseReasons.CANCEL
      ) {
        dispatch(categoryHook.cancel(formSource));
      } else if (treeId) {
        if (getCatalogsStateEntity(state, treeId)) {
          dispatch(catalogs.destroyTree({ catalogId: treeId }));
        } else if (getHierarchyStateEntity(state, treeId)) {
          dispatch(hierarchy.destroyTree({ categoryId: treeId }));
        }
      }

      if (
        parent?.type === FormParentType.DICTIONARY &&
        reason === FormCloseReasons.CANCEL
      ) {
        dispatch(dictionary.close({ id: parent.id }));
      }

      if (
        (formSource.type === FormSourceType.FORM &&
          reason === FormCloseReasons.CANCEL) ||
        formSource.type === FormSourceType.PRINT
      ) {
        const openMode = getCategoryOpenMode(state, formSource.categoryId);
        if (openMode === CategoryOpenMode.HIDDEN) {
          dispatch(
            categoryActions.close({
              categoryId: formSource.categoryId,
            })
          );
        }
      }
    }
  )
);

formListener.startListening(
  apiCall(
    {
      request: form.submit,
      success: form.submitSuccess,
      failure: form.submitFailure,
    },
    async ({ formId }, state, dispatch) => {
      const parent = getFormParent(state, formId);
      const command = getFormCommandName(state, formId);
      const formState = getFormState(state, formId);
      const context = getFormContext(state, formId);

      let args = { ...formState.args };

      if (isConditionForm(formState.source)) {
        if (parent?.type === FormParentType.DATASET) {
          const valuableArgs: TActionArgsValues =
            conditionsService.sanitizeConditionArgs(
              args,
              formState.source.conditions,
              formState.controls
            );

          dispatch(
            categoryActions.updateLocalConditions({
              categoryId: formState.source.categoryId,
              datasetName: getDatasetName(state, parent.id),
              conditions: {
                queryOptions: { caseSensitive: false },
                conditions: valuableArgs,
              },
            })
          );
        }
      }

      const controls = Object.values(formState.controls);
      const filesToUpload = controls.filter(
        control =>
          (control.type === FormControlViewModelType.FILE ||
            (control.type === FormControlViewModelType.IMAGE &&
              control.changed)) &&
          control.value
      ) as Array<
        FileInputFormControlViewModel | ImageInputFormControlViewModel
      >;

      let ids: Record<TControlName, IStreamDeclaration> = {};

      if (filesToUpload.length > 0) {
        const files: Record<string, File> = filesToUpload.reduce(
          (memo, control) => ({
            ...memo,
            [control.name]: control.value,
          }),
          {}
        );

        ids = await uploadFormFiles(files);
        args = { ...args, ...ids };
      }

      for (const control of controls) {
        if (
          control.type === FormControlViewModelType.FILE ||
          control.type === FormControlViewModelType.IMAGE ||
          !control.hasValue ||
          (tg.isSomething(args[control.name]) &&
            !args[control.name].field &&
            !args[control.name].domain)
        ) {
          continue;
        }

        if (
          tg.isSomething(args[control.name]) &&
          args[control.name].context &&
          context
        ) {
          args[control.name] = context.get(args[control.name].context);
        } else {
          args[control.name] = control.value;
        }
      }

      for (const arg of Object.keys(args)) {
        if (tg.isSomething(args[arg]) && args[arg].context && context) {
          args[arg] = context.get(args[arg].context);
        }

        if (tg.isSomething(args[arg]) && args[arg].domain) {
          args[arg] = null;
        }
      }

      for (const control of controls) {
        if ('trim' in control && control.trim && args[control.name]) {
          args[control.name] = trimControlValue(
            control.trim,
            args[control.name]
          );
        }
      }

      const hook =
        isConditionForm(formState.source) ||
        formState.source.type === FormSourceType.FORM
          ? formState.source.hook
          : undefined;

      if (
        command === '' &&
        !isConditionForm(formState.source) &&
        !isPredefinedForm(formState.source)
      ) {
        dispatch(
          actions.continueMultistep({
            action: formState.source.action,
            args,
            source: formState.source,
            context,
            hook: !!hook,
            formId,
          })
        );
      } else if (command && parent?.type === FormParentType.DATASET) {
        dispatch(
          commands.execute({
            datasetId: parent.id,
            command,
            args: hiddenFieldsInRows.delete(args),
            formId,
            hook: hook ? HookTypes.OPEN : undefined,
          })
        );
      } else if (parent?.type === FormParentType.DICTIONARY) {
        dispatch(
          dictionary.submit({
            id: parent.id,
            value: getControlValue(
              state,
              parent.id,
              TreeFormFieldNames.TARGET_ID
            ),
          })
        );
        dispatch(form.close({ formId }));
      }
    }
  )
);

formListener.startListening(
  postDispatch(form.createSuccess, ({ form: createdForm }, state, dispatch) => {
    if (isPredefinedForm(createdForm.source)) {
      const { controls, id } = createdForm;
      Object.values(controls).forEach(control => {
        if (control.hasValue) {
          validateField(
            { formId: id, field: control.name, value: control.value },
            state,
            dispatch
          );
        }
      });
    }
  })
);

export default formListener.middleware;
